import React from 'react';

import type {
  SkeletonProps,
  SkeletonTextProps,
  SkeletonAvatarProps,
  SkeletonCardProps,
  SkeletonVariant,
} from './Progress.types';

/**
 * Animation configuration for skeleton components
 */
const animationConfig = {
  pulse: 'animate-pulse',
  shimmer: 'animate-shimmer',
  none: '',
};

/**
 * Variant styling for base Skeleton component
 */
const variantConfig: Record<SkeletonVariant, string> = {
  text: 'rounded',
  circular: 'rounded-full',
  rectangular: 'rounded-none',
  rounded: 'rounded-lg',
};

/**
 * Convert dimension value to CSS
 */
const getDimensionStyle = (
  value: string | number | undefined
): string | undefined => {
  if (value === undefined) return undefined;
  if (typeof value === 'number') return `${value}px`;
  return value;
};

/**
 * Base Skeleton component for loading placeholders.
 * Shows an animated placeholder while content is loading.
 */
const Skeleton = React.forwardRef<HTMLDivElement, SkeletonProps>(
  (
    {
      variant = 'text',
      width,
      height,
      animation = 'pulse',
      className = '',
      style,
      ...props
    },
    ref
  ) => {
    const baseClasses = [
      'bg-contrast-600',
      variantConfig[variant],
      animationConfig[animation],
      className,
    ].join(' ');

    // Default heights based on variant
    const defaultHeight = variant === 'text' ? '1em' : undefined;
    const defaultWidth = variant === 'circular' ? height : undefined;

    const computedStyle: React.CSSProperties = {
      width: getDimensionStyle(width) ?? getDimensionStyle(defaultWidth),
      height: getDimensionStyle(height) ?? defaultHeight,
      ...style,
    };

    return (
      <div
        ref={ref}
        className={baseClasses}
        style={computedStyle}
        aria-hidden="true"
        {...props}
      />
    );
  }
);

Skeleton.displayName = 'Skeleton';

/**
 * SkeletonText component for multi-line text placeholders.
 */
const SkeletonText = React.forwardRef<HTMLDivElement, SkeletonTextProps>(
  (
    {
      lines = 3,
      lastLineWidth = '70%',
      spacing = 'md',
      animation = 'pulse',
      className = '',
      ...props
    },
    ref
  ) => {
    const spacingConfig = {
      sm: 'space-y-1.5',
      md: 'space-y-2',
      lg: 'space-y-3',
    };

    const heightConfig = {
      sm: 'h-3',
      md: 'h-4',
      lg: 'h-5',
    };

    return (
      <div
        ref={ref}
        className={`${spacingConfig[spacing]} ${className}`}
        aria-hidden="true"
        {...props}
      >
        {Array.from({ length: lines }).map((_, index) => {
          const isLastLine = index === lines - 1;
          const width = isLastLine ? getDimensionStyle(lastLineWidth) : '100%';

          return (
            <div
              key={index}
              className={[
                'bg-contrast-600 rounded',
                heightConfig[spacing],
                animationConfig[animation],
              ].join(' ')}
              style={{ width }}
            />
          );
        })}
      </div>
    );
  }
);

SkeletonText.displayName = 'SkeletonText';

/**
 * Avatar size configuration
 */
const avatarSizeConfig = {
  xs: 'w-6 h-6',
  sm: 'w-8 h-8',
  md: 'w-10 h-10',
  lg: 'w-12 h-12',
  xl: 'w-16 h-16',
};

/**
 * SkeletonAvatar component for avatar placeholders.
 */
const SkeletonAvatar = React.forwardRef<HTMLDivElement, SkeletonAvatarProps>(
  ({ size = 'md', animation = 'pulse', className = '', ...props }, ref) => {
    const classes = [
      'rounded-full bg-contrast-600',
      avatarSizeConfig[size],
      animationConfig[animation],
      className,
    ].join(' ');

    return <div ref={ref} className={classes} aria-hidden="true" {...props} />;
  }
);

SkeletonAvatar.displayName = 'SkeletonAvatar';

/**
 * SkeletonCard component for card content placeholders.
 * Provides a pre-configured skeleton layout for common card patterns.
 */
const SkeletonCard = React.forwardRef<HTMLDivElement, SkeletonCardProps>(
  (
    {
      showHeader = true,
      showAvatar = false,
      lines = 3,
      showFooter = false,
      animation = 'pulse',
      className = '',
      ...props
    },
    ref
  ) => {
    const containerClasses = [
      'bg-contrast-700 rounded-xl border border-stroke-subtle',
      'p-4 sm:p-6',
      className,
    ].join(' ');

    return (
      <div ref={ref} className={containerClasses} aria-hidden="true" {...props}>
        {/* Header */}
        {showHeader && (
          <div className="flex items-center gap-3 mb-4">
            {showAvatar && <SkeletonAvatar size="md" animation={animation} />}
            <div className="flex-1 space-y-2">
              <Skeleton
                variant="text"
                width={showAvatar ? '60%' : '40%'}
                height="1.25rem"
                animation={animation}
              />
              {showAvatar && (
                <Skeleton
                  variant="text"
                  width="30%"
                  height="0.875rem"
                  animation={animation}
                />
              )}
            </div>
          </div>
        )}

        {/* Content */}
        <SkeletonText lines={lines} spacing="md" animation={animation} />

        {/* Footer */}
        {showFooter && (
          <div className="flex items-center gap-3 mt-4 pt-4 border-t border-stroke-subtle">
            <Skeleton
              variant="rounded"
              width={80}
              height={36}
              animation={animation}
            />
            <Skeleton
              variant="rounded"
              width={80}
              height={36}
              animation={animation}
            />
          </div>
        )}
      </div>
    );
  }
);

SkeletonCard.displayName = 'SkeletonCard';

export { Skeleton, SkeletonText, SkeletonAvatar, SkeletonCard };
export default Skeleton;
